/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Zone de saisie intelligente pour l'Assistant Juridique IA :
 *  - Multiligne avec auto-resize
 *  - Limite stricte de 1 000 caractères avec compteur en temps réel
 *  - Soumission par Ctrl+Entrée ou Cmd+Entrée
 *  - Entrée seule = retour à la ligne
 *  - Bouton "✨ Analyser mon cas"
 */

import React, { useRef, useEffect } from 'react';
import { Sparkles, ArrowRight, CornerDownLeft, AlertCircle, XCircle } from 'lucide-react';

interface LegalQuestionInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (question: string) => void;
  isLoading?: boolean;
  onCancel?: () => void;
  variant?: 'hero' | 'search';
  placeholder?: string;
}

const MAX_CHARACTERS = 1000;

export const LegalQuestionInput: React.FC<LegalQuestionInputProps> = ({
  value,
  onChange,
  onSubmit,
  isLoading = false,
  onCancel,
  variant = 'search',
  placeholder = "Exemple : Mon propriétaire refuse de me restituer ma caution alors que j'ai quitté le logement sans dégradation. Quels sont mes droits et quels textes juridiques puis-je invoquer ?",
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isHero = variant === 'hero';

  // Auto-resize du textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 240;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARACTERS) {
      onChange(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!isLoading && value.trim().length > 0) {
        onSubmit(value.trim());
      }
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoading && value.trim().length > 0) {
      onSubmit(value.trim());
    }
  };

  const charCount = value.length;
  const isNearLimit = charCount > 900;
  const isAtLimit = charCount >= MAX_CHARACTERS;
  const isValid = value.trim().length > 0;

  return (
    <form onSubmit={handleFormSubmit} className="w-full space-y-2">
      {/* Title above textarea */}
      <div className="flex items-center justify-between px-1">
        <label
          htmlFor="ai-legal-question-input"
          className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${
            isHero ? 'text-white/90 drop-shadow-xs' : 'text-zinc-700'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          <span>Décrivez votre situation juridique</span>
        </label>

        {/* Live Character counter */}
        <span
          className={`text-[11px] font-mono font-semibold transition-colors ${
            isAtLimit
              ? 'text-red-500 font-bold'
              : isNearLimit
                ? 'text-amber-500'
                : isHero
                  ? 'text-white/70'
                  : 'text-zinc-400'
          }`}
        >
          {charCount} / {MAX_CHARACTERS} caractères
        </span>
      </div>

      {/* Main Container Card */}
      <div
        className={`relative rounded-3xl transition-all border ${
          isHero
            ? 'bg-white/95 backdrop-blur-md border-white/60 shadow-xl focus-within:ring-2 focus-within:ring-amber-400'
            : 'bg-white border-zinc-200 shadow-md focus-within:border-primary-750/60 focus-within:ring-3 focus-within:ring-primary-750/10'
        }`}
      >
        <textarea
          id="ai-legal-question-input"
          ref={textareaRef}
          rows={3}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          placeholder={placeholder}
          aria-label="Description de votre situation juridique"
          maxLength={MAX_CHARACTERS}
          className="w-full pt-4 pb-3 px-5 text-sm sm:text-[15px] text-zinc-900 leading-relaxed placeholder:text-zinc-400 focus:outline-none resize-none bg-transparent rounded-t-3xl min-h-[90px] max-h-[240px] custom-scrollbar disabled:opacity-60"
        />

        {/* Bottom Actions Row */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-zinc-100 bg-zinc-50/60 rounded-b-3xl">
          {/* Helper indication & shortcut info */}
          <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 hidden sm:flex">
            <span>Appuyez sur</span>
            <kbd className="px-1.5 py-0.5 bg-white border border-zinc-200 text-zinc-700 font-mono text-[10px] rounded shadow-2xs font-bold">
              Ctrl
            </kbd>
            <span>+</span>
            <kbd className="px-1.5 py-0.5 bg-white border border-zinc-200 text-zinc-700 font-mono text-[10px] rounded shadow-2xs font-bold flex items-center gap-0.5">
              <CornerDownLeft className="w-2.5 h-2.5" /> Entrée
            </kbd>
            <span>pour lancer</span>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {/* Cancel action if loading */}
            {isLoading && onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-xl transition cursor-pointer border border-red-200 flex items-center gap-1.5"
              >
                <XCircle className="w-4 h-4" />
                <span>Annuler</span>
              </button>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className={`px-5 py-2.5 rounded-2xl text-xs font-bold tracking-wide transition-all flex items-center gap-2 cursor-pointer border-none shadow-md ${
                !isValid || isLoading
                  ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed shadow-none'
                  : 'bg-gradient-to-r from-[#1C0132] to-[#3D0F73] hover:from-[#2a034b] hover:to-[#4e1591] text-white shadow-primary-750/25 active:scale-98'
              }`}
            >
              {isLoading ? (
                <>
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  <span>Analyse en cours...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  <span>Analyser mon cas</span>
                  <ArrowRight className="w-3.5 h-3.5 text-white/80" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Disclaimers & Notes */}
      <div className="px-2 space-y-1">
        <p
          className={`text-[11px] leading-relaxed flex items-start gap-1.5 ${
            isHero ? 'text-white/80' : 'text-zinc-500'
          }`}
        >
          <span className="text-amber-500 shrink-0 font-bold">ℹ</span>
          <span>
            L'assistant recherche les dispositions juridiques pertinentes dans la documentation officielle de Docarn Juridique.
          </span>
        </p>
        <p
          className={`text-[10px] leading-relaxed italic ${
            isHero ? 'text-white/60' : 'text-zinc-400'
          }`}
        >
          Les réponses générées par l'IA sont fournies à titre informatif et doivent être vérifiées à partir des sources juridiques citées.
        </p>
      </div>
    </form>
  );
};

export default LegalQuestionInput;
